import type { ReactNode } from 'react';
import * as TanStackTable from '@tanstack/react-table';
import type { DataGridColumnMeta } from '../data-grid/data-grid-column-meta';

/** One line of data a column can show, for example the driver's phone in the Driver column. */
export type ColumnField = {
  /**
   * ISO date (`YYYY-MM-DD`) the field was added, for one added after its table's
   * `knownColumnsRollout`: a stored configuration shows it only once it lists it. Keep it for good,
   * as a column's `addedOn`.
   */
  addedOn?: string;
  /** Always shown, such as a locked column's own value: Field selection cannot untick it. */
  fixed?: true;
  id: string;
  label: string;
};

/** The field ids of a Column field list declared `as const`, so a misspelt id fails type-check. */
export type ColumnFieldId<TFields extends readonly ColumnField[]> = TFields[number]['id'];

/**
 * How a column shows its data where a Column field cannot express it, for example a date as
 * absolute or relative. `default` is today's look, which a view that never chose one shows.
 */
export type DisplayChoice<TValue extends string = string> = {
  default: TValue;
  /** The Column field it changes, if one; table settings offer it only while that field shows. */
  field?: string;
  id: string;
  label: string;
  /** Each option; an `icon`, such as a glyph of the lines it shows, leads its label. */
  options: readonly {
    icon?: ReactNode;
    label: string;
    value: TValue;
  }[];
};

/**
 * The options of a Display choice that shows or hides part of a cell. Table settings offer a choice
 * with these options, in this order, as one "Show …" checkbox; its label names the part, read after
 * "Show" or "Hide".
 */
export const SHOW_OR_HIDE = [{
  label: 'Show',
  value: 'show'
}, {
  label: 'Hide',
  value: 'hide'
}] as const;
export interface TableColumnMeta extends DataGridColumnMeta<TanStackTable.RowData> {
  /**
   * ISO date (`YYYY-MM-DD`) the column was added, for a column added after its table's
   * `knownColumnsRollout`. A stored configuration shows it only when it stores `true` for it, and
   * table settings mark it New for a while (`isMarkedNew`). Keep it for good: removing it makes the column show
   * in every configuration that never chose it.
   */
  addedOn?: string;
  align?: 'center' | 'end' | 'start';
  /**
   * Makes the whole cell navigate to the returned href. Hover triggers such as tooltips keep
   * working inside a linked cell. Never set it on a column whose cells hold their own actions
   * (buttons, popovers, drawers, nested links).
   */
  cellLink?: TableCellLink;
  /** One line on what the column shows, above its preview in the Add column menu. */
  description?: string;
  /**
   * The column's Display choices. Each view stores the option it chose for each, else the default,
   * which the cell reads with `useDisplayChoice`.
   */
  displayChoices?: readonly DisplayChoice[];
  /**
   * The Column fields of a multi-line cell, two or more, in display order: the first shown is the
   * primary line. Each view's Field selection picks which show; `StackedCell` reads it, and other
   * cells read it with `useFieldSelection`. A List cell's fields are the parts of each item, which
   * `useListItems` shows.
   */
  fields?: readonly ColumnField[];
  /**
   * Positions the cell so its own control can stretch over the whole cell, like `cellLink` does,
   * for a column whose cells act instead of navigating.
   */
  fillsCell?: boolean;
  /**
   * The column's category in table settings. A table whose columns have categories lists only
   * its shown columns, and adds hidden ones from an Add column menu grouped by category.
   */
  group?: TableColumnGroup;
  /**
   * Whether a row's data has a value for this column, so the Add column preview can show it as
   * an example. Defaults to the accessor value being present. Set it when the cell reads other
   * fields than its accessor, or always draws a control.
   */
  hasValue?: (original: TanStackTable.RowData) => boolean;
  /**
   * Renders a function header as a control in the actions column.
   * String headers stay a title.
   */
  headerControl?: boolean;
  /** Stable, user-facing label for table settings. */
  label?: string;
  /**
   * Marks a List cell, whose lines are repeated items of one type. Each view stores its Items shown
   * (1 or 2), which the cell reads with `useItemsShown` for Comfortable rows. It shows 2 unless
   * `defaultItemsShown` keeps a cell's earlier look. Its `fields`, if any, pick the parts of each item.
   */
  listCell?: boolean | {
    defaultItemsShown: 1 | 2;
  };
  /** Prevents a column from leaving its pinned edge in table settings. */
  lockedPinning?: 'end' | 'start';
  /**
   * The header title in Condensed and Dense rows, whose header keeps one line, for a column whose
   * title does not fit its default width. The header's tooltip shows the full title, which table
   * settings, filters and sorting keep.
   */
  shortLabel?: string;
}
export const tableFeatures = TanStackTable.tableFeatures({
  // Cell selection, faceting, expanding, pagination and row pinning are registered only because
  // the ReUI data grid calls their methods. No row models are registered for them, and
  // TableStateProvider keeps pagination, expanding and cell selection from acting on our rows.
  cellSelectionFeature: TanStackTable.cellSelectionFeature,
  columnFacetingFeature: TanStackTable.columnFacetingFeature,
  columnFilteringFeature: TanStackTable.columnFilteringFeature,
  // SAFETY: `columnMeta` is TanStack Table's type-only slot for column meta; the value is never
  // read, so the empty object only carries the TableColumnMeta type.
  columnMeta: {} as TableColumnMeta,
  columnOrderingFeature: TanStackTable.columnOrderingFeature,
  columnPinningFeature: TanStackTable.columnPinningFeature,
  columnResizingFeature: TanStackTable.columnResizingFeature,
  columnSizingFeature: TanStackTable.columnSizingFeature,
  columnVisibilityFeature: TanStackTable.columnVisibilityFeature,
  rowExpandingFeature: TanStackTable.rowExpandingFeature,
  rowPaginationFeature: TanStackTable.rowPaginationFeature,
  rowPinningFeature: TanStackTable.rowPinningFeature,
  rowSelectionFeature: TanStackTable.rowSelectionFeature,
  rowSortingFeature: TanStackTable.rowSortingFeature
});
export type Cell<TData extends TanStackTable.RowData = TanStackTable.RowData, TValue extends TanStackTable.CellData = TanStackTable.CellData> = TanStackTable.Cell<TableFeatures, NormalizedRowData<TData>, TValue>;
export type Column<TData extends TanStackTable.RowData = TanStackTable.RowData, TValue extends TanStackTable.CellData = TanStackTable.CellData> = TanStackTable.Column<TableFeatures, NormalizedRowData<TData>, TValue>;
export type ColumnDef<TData extends TanStackTable.RowData = TanStackTable.RowData, TValue extends TanStackTable.CellData = TanStackTable.CellData> = TanStackTable.ColumnDef<TableFeatures, NormalizedRowData<TData>, TValue>;
export type ColumnHelper<TData extends TanStackTable.RowData = TanStackTable.RowData> = TanStackTable.ColumnHelper<TableFeatures, NormalizedRowData<TData>>;
export type Header<TData extends TanStackTable.RowData = TanStackTable.RowData, TValue extends TanStackTable.CellData = TanStackTable.CellData> = TanStackTable.Header<TableFeatures, NormalizedRowData<TData>, TValue>;
export type HeaderGroup<TData extends TanStackTable.RowData = TanStackTable.RowData> = TanStackTable.HeaderGroup<TableFeatures, NormalizedRowData<TData>>;
export interface PersistedColumnPinningState {
  left?: string[];
  right?: string[];
}
export type Row<TData extends TanStackTable.RowData = TanStackTable.RowData> = TanStackTable.Row<TableFeatures, NormalizedRowData<TData>>;
export type Table<TData extends TanStackTable.RowData = TanStackTable.RowData, TSelected = TanStackTable.TableState<TableFeatures>> = TanStackTable.ReactTable<TableFeatures, NormalizedRowData<TData>, TSelected>;
export type TableCellLink = (props: {
  cell: Cell;
  context: unknown;
  row: Row;
}) => null | string | undefined;

/** A category of columns in table settings; the Add column menu lists categories by `order`. */
export type TableColumnGroup = {
  label: string;
  order: number;
};
export type TableColumnPinningState = TanStackTable.ColumnPinningState;
export type TableFeatures = typeof tableFeatures;
export type TableOptions<TData extends TanStackTable.RowData = TanStackTable.RowData, TValue extends TanStackTable.CellData = TanStackTable.CellData> = Omit<TanStackTable.TableOptions<TableFeatures, NormalizedRowData<TData>>, 'columns'> & {
  columns: readonly TanStackTable.ColumnDef<TableFeatures, NormalizedRowData<TData>, TValue>[];
};
type NormalizedRowData<TData extends TanStackTable.RowData> = TData;
export const toTableColumnPinning = (pinning: PersistedColumnPinningState | undefined): TableColumnPinningState => ({
  end: pinning?.right ?? [],
  start: pinning?.left ?? []
});
export const toPersistedColumnPinning = (pinning: TableColumnPinningState): PersistedColumnPinningState => ({
  left: pinning.start,
  right: pinning.end
});
export const createColumnHelper = <TData extends TanStackTable.RowData = TanStackTable.RowData,>(): ColumnHelper<TData> => TanStackTable.createColumnHelper<TableFeatures, NormalizedRowData<TData>>();
export const useTable = (options, selector) => {
  return TanStackTable.useTable(options as TanStackTable.TableOptions<TableFeatures, NormalizedRowData<TData>>, selector);
};
export const flexRender = TanStackTable.flexRender;
export type { CellData, ColumnFilter, ColumnOrderState, ColumnSizingState, RowData, RowSelectionState, SortingState, TableState } from '@tanstack/react-table';